import { useState, useCallback } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion } from 'framer-motion';
import { ShieldCheck, X, Delete } from 'lucide-react';
import * as dbOps from './db/kioskDbOperations';

/**
 * Admin login overlay — shown when the gear icon is tapped on the idle screen.
 * Accepts a 6-digit admin code to unlock the admin panel.
 */
export default function KioskAdminLogin({ onSuccess, onCancel }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);

  const handleDigit = useCallback((digit) => {
    setError('');
    setCode((prev) => {
      if (prev.length >= 6) return prev;
      return prev + digit;
    });
  }, []);

  const handleDelete = useCallback(() => {
    setCode((prev) => prev.slice(0, -1));
    setError('');
  }, []);

  const handleClear = useCallback(() => {
    setCode('');
    setError('');
  }, []);

  const handleSubmit = useCallback(async () => {
    if (code.length < 4) {
      setError('Code too short');
      return;
    }

    const storedCode = await dbOps.getConfig('admin_code');

    if (!storedCode) {
      // No admin code set — allow access (first-time setup)
      onSuccess?.();
      return;
    }

    if (code === storedCode) {
      onSuccess?.();
    } else {
      setError('Invalid admin code');
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setCode('');
    }
  }, [code, onSuccess]);

  // Auto-submit when 6 digits entered
  const handleDigitWithAutoSubmit = useCallback((digit) => {
    setError('');
    setCode((prev) => {
      const next = prev + digit;
      if (next.length >= 6) {
        // Schedule submit after state update
        setTimeout(async () => {
          const storedCode = await dbOps.getConfig('admin_code');
          if (!storedCode || next === storedCode) {
            onSuccess?.();
          } else {
            setError('Invalid admin code');
            setShake(true);
            setTimeout(() => setShake(false), 500);
            setCode('');
          }
        }, 100);
      }
      return next.length > 6 ? prev : next;
    });
  }, [onSuccess]);

  const digits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'CLR', '0', 'DEL'];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className={`bg-white rounded-3xl shadow-2xl p-8 w-full max-w-sm mx-4 ${shake ? 'animate-shake' : ''}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-7 h-7 text-slate-700" />
            <h2 className="text-xl font-bold text-slate-800">Admin Access</h2>
          </div>
          <button
            onClick={onCancel}
            className="p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Code dots */}
        <div className="flex justify-center gap-3 mb-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className={`w-4 h-4 rounded-full transition-colors ${
                i < code.length ? 'bg-slate-800' : 'bg-slate-200'
              }`}
            />
          ))}
        </div>

        {/* Error */}
        {error && (
          <p className="text-center text-sm text-red-500 font-medium mb-4">{error}</p>
        )}

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-3">
          {digits.map((d) => {
            if (d === 'CLR') {
              return (
                <button
                  key={d}
                  onClick={handleClear}
                  className="h-16 rounded-xl bg-slate-100 text-slate-500 text-sm font-semibold active:bg-slate-200 transition-colors"
                >
                  CLR
                </button>
              );
            }
            if (d === 'DEL') {
              return (
                <button
                  key={d}
                  onClick={handleDelete}
                  className="h-16 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center active:bg-slate-200 transition-colors"
                >
                  <Delete className="w-5 h-5" />
                </button>
              );
            }
            return (
              <button
                key={d}
                onClick={() => handleDigitWithAutoSubmit(d)}
                className="h-16 rounded-xl bg-slate-50 text-slate-800 text-2xl font-semibold hover:bg-slate-100 active:bg-slate-200 transition-colors"
              >
                {d}
              </button>
            );
          })}
        </div>
      </motion.div>
    </motion.div>
  );
}
